import {
  buildConversationPath,
  buildNotificationSoundPath,
  buildThreadPath,
  type NotificationSoundName,
  type RealtimeEvent,
} from '@threadline/shared'

/**
 * Announces a genuinely new incoming message with a sound and an operating system
 * notification.
 *
 * It reacts only to live Server-Sent Events. History pages, reconnection refetches and
 * page reloads never pass through here, which is what keeps a refresh from replaying
 * every message the person has already seen.
 */

/**
 * The identifiers already announced. The same event can be delivered more than once —
 * a duplicate stream, a reconnect that repeats an event — and each message must still
 * produce exactly one sound and one notification.
 */
const announcedMessageIds = new Set<number>()
const MAXIMUM_REMEMBERED_MESSAGE_IDS = 200

function rememberAnnouncedMessage(messageId: number): boolean {
  if (announcedMessageIds.has(messageId)) {
    return false
  }
  announcedMessageIds.add(messageId)

  // Only the recent past matters: an identifier old enough to fall out of this set can no
  // longer arrive as a live event.
  if (announcedMessageIds.size > MAXIMUM_REMEMBERED_MESSAGE_IDS) {
    const oldestMessageId = announcedMessageIds.values().next().value
    if (oldestMessageId !== undefined) {
      announcedMessageIds.delete(oldestMessageId)
    }
  }
  return true
}

/** Exposed so tests start from a known state; the running application never calls it. */
export function forgetAnnouncedMessages(): void {
  announcedMessageIds.clear()
}

let notificationAudio: HTMLAudioElement | null = null

/**
 * Browsers refuse to play audio until the person has interacted with the page. The first
 * interaction plays the sound silently to lift that restriction, so the first real
 * message is already audible.
 */
function unlockAudioPlaybackOnFirstInteraction(audioElement: HTMLAudioElement): void {
  const unlockPlayback = () => {
    const originalVolume = audioElement.volume
    audioElement.volume = 0
    void audioElement
      .play()
      .then(() => {
        audioElement.pause()
        audioElement.currentTime = 0
      })
      .catch(() => undefined)
      .finally(() => {
        audioElement.volume = originalVolume
      })
  }

  for (const interactionEventName of ['pointerdown', 'keydown']) {
    window.addEventListener(interactionEventName, unlockPlayback, { once: true })
  }
}

function prepareNotificationSound(soundName: NotificationSoundName): void {
  if (notificationAudio !== null || typeof Audio === 'undefined') {
    return
  }
  notificationAudio = new Audio(buildNotificationSoundPath(soundName))
  notificationAudio.preload = 'auto'
  unlockAudioPlaybackOnFirstInteraction(notificationAudio)
}

function playNotificationSound(): void {
  if (notificationAudio === null) {
    return
  }
  notificationAudio.currentTime = 0
  // A browser that still refuses playback must never break the notification itself.
  void notificationAudio.play().catch(() => undefined)
}

/** Exposed for tests, which need each case to start without a retained audio element. */
export function resetNotificationSound(): void {
  notificationAudio = null
}

function buildDestinationPath(messageEvent: {
  counterpartAccountId: string
  counterpartAlias: string | null
  rootMessageId: number
  isThreadReply: boolean
}): string {
  return messageEvent.isThreadReply
    ? buildThreadPath(
        messageEvent.counterpartAccountId,
        messageEvent.counterpartAlias,
        messageEvent.rootMessageId,
      )
    : buildConversationPath(messageEvent.counterpartAccountId, messageEvent.counterpartAlias)
}

export type IncomingMessageNotificationDependencies = {
  viewerHandle: () => string | null
  /**
   * Whether this conversation was muted or archived by the person about to be interrupted.
   * Asked by account id, which a group has and a handle is not.
   */
  conversationStaysQuiet: (counterpartAccountId: string) => boolean
  soundName: () => NotificationSoundName
  translate: (localizationKey: string, namedValues?: Record<string, unknown>) => string
  openConversation: (destinationPath: string, messageId: number) => void
}

export function useIncomingMessageNotifications(
  dependencies: IncomingMessageNotificationDependencies,
) {
  function showDesktopNotification(messageEvent: Extract<RealtimeEvent, { eventType: 'message-created' }>): void {
    // Permission is requested from Settings, never from an arriving message: a browser
    // would refuse the request anyway without a user gesture behind it.
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return
    }

    const previewText =
      messageEvent.messagePreview.length > 0
        ? messageEvent.messagePreview
        : dependencies.translate('notification.newMessageWithAttachmentsOnly')

    try {
      const desktopNotification = new Notification(
        dependencies.translate('notification.youReceivedANewMessage', {
          senderName: messageEvent.senderDisplayName,
        }),
        {
          body: previewText,
          icon: '/icons/application-icon-192.png',
          // The same tag the pushed notification uses, so a message that arrives through
          // both channels replaces itself instead of appearing twice.
          tag: `threadline-conversation-${messageEvent.counterpartAccountId}`,
        },
      )

      desktopNotification.onclick = () => {
        window.focus()
        desktopNotification.close()
        dependencies.openConversation(buildDestinationPath(messageEvent), messageEvent.messageId)
      }
    } catch (notificationFailure) {
      console.warn('The message notification could not be shown', notificationFailure)
    }
  }

  function announceRealtimeEvent(realtimeEvent: RealtimeEvent): void {
    if (realtimeEvent.eventType !== 'message-created') {
      return
    }
    // Nobody is told about a message they just sent themselves.
    if (realtimeEvent.senderHandle === dependencies.viewerHandle()) {
      return
    }
    if (!rememberAnnouncedMessage(realtimeEvent.messageId)) {
      return
    }
    // The event still arrives, and the screen and the unread count still use it. What
    // muting stops is the interruption, not the knowing.
    if (dependencies.conversationStaysQuiet(realtimeEvent.counterpartAccountId)) {
      return
    }

    prepareNotificationSound(dependencies.soundName())
    playNotificationSound()
    showDesktopNotification(realtimeEvent)
  }

  return { announceRealtimeEvent, prepareNotificationSound }
}
